import React, { useContext, useState, useEffect } from 'react';
import DialogBox from '../../Common/Layout/DialogBox';
import { Box } from '@mui/material';
import { InvoiceStatus } from '../../../enums/InvoiceStatusEnums';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import {
  IContact,
  IInvoice,
  IInvoiceInput,
  IPaymentDetails,
  useCreateInvoiceMutation,
  usePaymentDetailsLazyQuery,
  useUpdateInvoiceMutation,
  useContactByEmailAndFullNameQuery
} from '../../../generated/graphql';
import PaymentDetailsStep from './PaymentDetailsStep';
import CancelButton from '../../Common/Buttons/CancelButton';
import SaveButton from '../../Common/Buttons/SaveButton';
import { InvoiceType } from '../../../models/invoice';
import ClientStep from './ClientStep';
import ProductAndPricingStep from './ProductAndPricingStep';
import InvoiceStep from './InvoiceStep';
import { getInvoiceProductInfoDto } from '../InvoiceList';
import { canUserEdit } from '../../../utils/rightUtils';
import { Types } from '../../../enums/TypeEnums';
import Loader from '../../Common/Loader';
import FormStepper from '../../Common/Layout/FormStepper';

const steps = ['My Details', 'Client', 'Product & Pricing', 'Invoice'];

export interface InvoiceErrors {
  company?: string;
  contact?: string;
  address?: string;
  product?: string;
}

interface IProps {
  onClose: () => void;
  addNewInvoice?: (invoice: IInvoice) => void;
  editInvoice?: (invoice: IInvoice) => void;
  cventContact?: IContact | null;
}

const InvoiceStepper: React.FunctionComponent<IProps> = ({
  onClose,
  addNewInvoice,
  editInvoice,
  cventContact
}: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [activeStep, setActiveStep] = useState(
    state.selectedInvoice?.status === InvoiceStatus.New ? 1 : 3
  );
  const [getDbPaymentDetails, dbPaymentDetails] = usePaymentDetailsLazyQuery({
    fetchPolicy: 'no-cache'
  });
  const [createInvoice, createInvoiceResult] = useCreateInvoiceMutation();
  const [updateInvoice, updateInvoiceResult] = useUpdateInvoiceMutation();
  const canEdit = canUserEdit(state.user, Types.Invoice);
  const [currentPoNumber, setCurrentPoNumber] = useState('');
  const [currentRemarks, setNewRemarks] = useState('');
  const [currentInvoiceContact, setCurrentInvoiceContact] = useState<IContact | null>(null);
  const isPoNumberEdited = currentPoNumber !== state.selectedInvoice?.po_nr;
  const isRemarkInputEdited = currentRemarks !== state.selectedInvoice?.remarks;
  const isContactEdited = currentInvoiceContact !== state.selectedInvoice?.contact;
  const [dbContact, setDbContact] = useState<IContact | null>(null);

  const dbContactData = useContactByEmailAndFullNameQuery({
    variables: {
      fullName: state.selectedInvoice?.contact?.fullName as string,
      email: state.selectedInvoice?.contact?.email as string
    },
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (dbContactData && dbContactData.data?.result) {
      setDbContact(dbContactData.data?.result as IContact);
    }
  }, [dbContactData.data?.result]);

  useEffect(() => {
    if (!state.selectedInvoice?.paymentDetails) {
      getDbPaymentDetails();
    }
    setCurrentPoNumber(state.selectedInvoice?.po_nr as string);
    setNewRemarks(state.selectedInvoice?.remarks as string);
    setCurrentInvoiceContact(state.selectedInvoice?.contact as IContact);
  }, []);

  useEffect(() => {
    if (dbPaymentDetails.data && dbPaymentDetails.data.result) {
      const data = dbPaymentDetails.data.result as IPaymentDetails;
      dispatch({
        type: Actions.SetInvoicePaymentDetails,
        payload: data
      });
    }
  }, [dbPaymentDetails.data]);

  const getStepContent = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return <PaymentDetailsStep />;
      case 1:
        return <ClientStep cventContact={cventContact} />;
      case 2:
        return <ProductAndPricingStep />;
      case 3:
        return <InvoiceStep editInvoice={editInvoice} dbContact={dbContact} />;
      default:
        return 'Unknown stepIndex';
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      const selectedInvoice = state.selectedInvoice as InvoiceType;
      if (selectedInvoice.isPaymentDetailsInEditMode) {
        dispatch({
          type: Actions.SetInvoiceErrors,
          payload: {
            ...selectedInvoice.errors,
            isPaymentDetailsInEditMode: true
          }
        });
      } else {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    } else if (activeStep === 1) {
      const { errors } = state.selectedInvoice as InvoiceType;
      let hasErrors =
        (errors.company && errors.company !== null) ||
        (errors.contact && errors.contact !== null) ||
        (errors.address && errors.address !== null);
      const stepErrors: InvoiceErrors = errors;
      if (!hasErrors) {
        if (!state.selectedInvoice?.company) {
          hasErrors = true;
          stepErrors.company = 'Select company';
        }
        if (!state.selectedInvoice?.contact) {
          hasErrors = true;
          stepErrors.contact = 'Select contact';
        }
        if (!state.selectedInvoice?.address) {
          hasErrors = true;
          stepErrors.address = 'Select address';
        }
      }

      if (hasErrors) {
        dispatch({
          type: Actions.SetInvoiceErrors,
          payload: {
            ...stepErrors
          }
        });
      } else {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    } else if (activeStep === 2) {
      if (!state.selectedInvoice?.productInfo?.product) {
        dispatch({
          type: Actions.SetInvoiceErrors,
          payload: {
            product: 'Select product'
          }
        });
      } else {
        dispatch({
          type: Actions.ResetInvoiceErrors
        });
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
    } else if (activeStep === 3) {
      const selectedInvoice = state.selectedInvoice as InvoiceType;
      if (selectedInvoice.status === InvoiceStatus.New) {
        if (isValidInvoice(selectedInvoice)) {
          const input = getInvoiceInputDto(selectedInvoice);
          createInvoice({
            variables: {
              input
            },
            update(_, { data }) {
              if (data?.result) {
                dispatch({
                  type: Actions.SetInvoice,
                  payload: {
                    invoice: data.result as IInvoice,
                    productInfo: getInvoiceProductInfoDto(data.result as IInvoice)
                  }
                });
                if (addNewInvoice) {
                  addNewInvoice(data.result as IInvoice);
                }
                dispatch({
                  type: Actions.ShowMessage,
                  payload: {
                    severity: 'success',
                    text: `You have successfully created new invoice`
                  }
                });
              }
            }
          }).catch((e) => {
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'error',
                text: e.message,
                autoHide: 6000
              }
            });
          });
        }
      } else if (
        state.selectedInvoice?.status === InvoiceStatus.Pending ||
        state.selectedInvoice?.status === InvoiceStatus.Confirmed
      ) {
        if (isValidInvoice(selectedInvoice)) {
          if (!hasInvoiceChanged(selectedInvoice) && !isPoNumberEdited && !isRemarkInputEdited) {
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'warning',
                text: `There is no new information to save`
              }
            });
          } else {
            updateInvoice({
              variables: {
                id: parseInt(selectedInvoice.invoice_full?.id as string),
                input: getInvoiceInputDto(selectedInvoice, selectedInvoice.invoice_full as IInvoice)
              },
              update(_, { data }) {
                if (data?.result) {
                  dispatch({
                    type: Actions.SetInvoice,
                    payload: {
                      invoice: data.result as IInvoice,
                      productInfo: getInvoiceProductInfoDto(data.result as IInvoice)
                    }
                  });
                  if (editInvoice) {
                    editInvoice(data.result as IInvoice);
                  }
                  dispatch({
                    type: Actions.ShowMessage,
                    payload: {
                      severity: 'success',
                      text: `You have successfully updated invoice`
                    }
                  });
                }
              }
            }).catch((e) => {
              dispatch({
                type: Actions.ShowMessage,
                payload: {
                  severity: 'error',
                  text: e.message,
                  autoHide: 6000
                }
              });
            });
          }
        }
      }
    }
  };

  const isValidInvoice = (selectedInvoice: InvoiceType) => {
    if (!selectedInvoice.productInfo) {
      return false;
    }
    if (!selectedInvoice.paymentDetails) {
      return false;
    }
    if (!selectedInvoice.company) {
      return false;
    }
    if (!selectedInvoice.contact) {
      return false;
    }
    if (!selectedInvoice.address) {
      return false;
    }
    return true;
  };

  const hasInvoiceChanged = (selectedInvoice: InvoiceType) => {
    const originalInvoice = selectedInvoice.invoice_full as IInvoice;
    // Payment Details Step
    if (selectedInvoice.paymentDetails?.id !== originalInvoice.paymentDetails.id) {
      return true;
    }
    // Client Step
    if (selectedInvoice.company?.name !== originalInvoice.details.company_name) {
      return true;
    }
    if (
      selectedInvoice.contact?.fullName !==
      `${originalInvoice.details.contact_first_name} ${originalInvoice.details.contact_last_name}`
    ) {
      return true;
    }
    if (selectedInvoice.contact.email !== originalInvoice.details.contact_email) {
      return true;
    }
    if (selectedInvoice.address?.name !== originalInvoice.details.address_name) {
      return true;
    }
    // Product Step
    if (selectedInvoice.productInfo?.product?.id !== originalInvoice.product.id) {
      return true;
    }
    if (selectedInvoice.productInfo.discount !== originalInvoice.discount) {
      return true;
    }
    if (selectedInvoice.productInfo.vatPercent !== originalInvoice.vat) {
      return true;
    }
    if (selectedInvoice.notes !== originalInvoice.notes) {
      return true;
    }
    return false;
  };

  const getInvoiceInputDto = (selectedInvoice: InvoiceType, originalInvoice?: IInvoice) => {
    const res = {
      product_id: parseInt(selectedInvoice.productInfo?.product?.id as string),
      membership_id: parseInt(state.selectedMembership?.id as string),
      payment_details_id: parseInt(selectedInvoice.paymentDetails?.id as string),
      total: selectedInvoice.productInfo?.total as number,
      discount: selectedInvoice.productInfo?.discount ?? 0,
      vat: selectedInvoice.productInfo?.vatPercent ?? 0,
      details: getPaymentDetailsDto(selectedInvoice, originalInvoice),
      notes: selectedInvoice.notes,
      po_nr: selectedInvoice.po_nr,
      remarks: selectedInvoice.remarks
    } as IInvoiceInput;
    return res;
  };

  const getPaymentDetailsDto = (selectedInvoice: InvoiceType, originalInvoice?: IInvoice) => {
    return {
      company_name: selectedInvoice.company?.name ?? originalInvoice?.details?.company_name,
      company_address_vat:
        selectedInvoice.company?.main_address?.complete_vat_number ??
        originalInvoice?.details?.company_address_vat,
      contact_first_name:
        selectedInvoice.contact?.first_name ?? originalInvoice?.details?.contact_first_name,
      contact_last_name:
        selectedInvoice.contact?.last_name ?? originalInvoice?.details?.contact_last_name,
      contact_email: selectedInvoice.contact?.email ?? originalInvoice?.details?.contact_email,
      address_name: selectedInvoice.address?.name ?? originalInvoice?.details?.address_name,
      street: selectedInvoice.address?.street ?? originalInvoice?.details?.street,
      city: selectedInvoice.address?.city ?? originalInvoice?.details?.city,
      zip: selectedInvoice.address?.zip ?? originalInvoice?.details?.zip,
      country: selectedInvoice.address?.country?.name ?? originalInvoice?.details?.country,
      region: selectedInvoice.address?.country?.region?.name ?? originalInvoice?.details?.region,
      title_external:
        selectedInvoice.invoice_full?.details.title_external ?? dbContact?.title_external,
      prefix: selectedInvoice.invoice_full?.details.prefix ?? dbContact?.prefix,
      vat_countrycode: originalInvoice?.details.vat_countrycode
    };
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleClose = () => {
    dispatch({
      type: Actions.ResetInvoice
    });
    onClose();
  };

  return (
    <DialogBox
      id="invoice-wizard-stepper"
      title={state.selectedInvoice?.status === InvoiceStatus.New ? 'Create Invoice' : 'Invoice'}
      width={1200}
      onClose={handleClose}
    >
      <FormStepper steps={steps} activeStep={activeStep} />
      <Box sx={{ minHeight: '370px' }}>{getStepContent(activeStep)}</Box>
      <Box
        className="centralize"
        sx={{ gridColumn: 'span 3', marginTop: '100px', marginBottom: '40px' }}
      >
        {activeStep !== 0 && (
          <CancelButton id="invoice-stepper-back-button" onClick={handleBack} text="Back" />
        )}
        <SaveButton
          id="invoice-stepper-save-next-button"
          disabled={
            activeStep === steps.length - 1 &&
            (!canEdit ||
              (state.selectedInvoice?.status !== InvoiceStatus.New &&
                state.selectedInvoice?.status !== InvoiceStatus.Pending &&
                !isPoNumberEdited &&
                !isRemarkInputEdited &&
                !isContactEdited))
          }
          onClick={handleNext}
          text={activeStep === steps.length - 1 ? 'Save' : 'Next'}
        />
      </Box>
      <Loader loading={createInvoiceResult.loading || updateInvoiceResult.loading} />
    </DialogBox>
  );
};

export default InvoiceStepper;
